import { useEffect, useState } from "react";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { API_BASE } from "../../../config";
import MeasuredChartFrame from "./MeasuredChartFrame";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const PURPLE = "#6F3AFF";
const BLUE = "#52ea91";
const TICK = { fill: "#d6d5ea", fontSize: 12 };

function formatDate(dateStr) {
  if (!dateStr) return "";

  const d = new Date(`${dateStr}T00:00:00`);

  if (Number.isNaN(d.getTime())) {
    return String(dateStr);
  }

  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function formatInt(n) {
  return Number(n ?? 0).toLocaleString("de-DE");
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeRows(raw) {
  const source = raw ?? {};
  const rows = Array.isArray(source)
    ? source
    : Array.isArray(source.data)
      ? source.data
      : Array.isArray(source.rows)
        ? source.rows
        : Array.isArray(source.items)
          ? source.items
          : [];

  return rows.map((d) => {
    const statuses = d.statuses || d.by_status || d;

    const open = toNumber(statuses.OPEN ?? statuses.open ?? d.OPEN ?? d.open, 0);
    const closed = toNumber(
      statuses.CLOSED ?? statuses.closed ?? d.CLOSED ?? d.closed,
      0
    );

    return {
      date: d.date || d.day || d.period || d.label || "",
      OPEN: open,
      CLOSED: closed,
      TOTAL: toNumber(
        statuses.TOTAL ?? statuses.total ?? d.TOTAL ?? d.total ?? d.total_cases,
        open + closed
      ),
    };
  });
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const open = payload.find((p) => p.dataKey === "OPEN");
  const closed = payload.find((p) => p.dataKey === "CLOSED");
  const total = payload.find((p) => p.dataKey === "TOTAL");

  return (
    <div className="remediation-dashboard-tooltip">
      <div className="remediation-dashboard-tooltip-title">
        {formatDate(label)}
      </div>

      {total && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 24,
            marginBottom: 4,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "#d6d5ea",
                display: "inline-block",
              }}
            />
            Total
          </span>
          <span style={{ fontWeight: 600 }}>{formatInt(total.value)}</span>
        </div>
      )}

      {open && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 24,
            marginBottom: 4,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: PURPLE,
                display: "inline-block",
              }}
            />
            Open
          </span>
          <span style={{ fontWeight: 600 }}>{formatInt(open.value)}</span>
        </div>
      )}

      {closed && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 24,
            marginBottom: 4,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: BLUE,
                display: "inline-block",
              }}
            />
            Closed
          </span>
          <span style={{ fontWeight: 600 }}>{formatInt(closed.value)}</span>
        </div>
      )}
    </div>
  );
}

export default function CaseTrendByStatus() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await fetchWithAuth(
          `${API_BASE}/ainstein/in/dashboard?chart_key=case_trend_by_status`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const statsData = await response.json();
        const normalizedRows = normalizeRows(statsData);

        if (!cancelled) {
          setRows(normalizedRows);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load status trend");
          setRows([]);
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="remediation-dashboard-section">
      <article className="remediation-dashboard-card">
        <h3 className="remediation-dashboard-card-title">Status Trend</h3>

        {loading && (
          <div className="remediation-dashboard-loading">
            <span className="remediation-dashboard-loading-spinner" />
            <span>Loading data...</span>
          </div>
        )}

        {!loading && (error || rows.length === 0) && (
          <div className="remediation-dashboard-empty-state">
            {error || "No data available"}
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <MeasuredChartFrame height={320}>
            {({ width, height }) => (
              <LineChart
                width={width}
                height={height}
                data={rows}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.06)"
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={TICK}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={TICK}
                  axisLine={false}
                  tickLine={false}
                  width={52}
                  tickFormatter={formatInt}
                />
                <Tooltip
                  cursor={{ stroke: "rgba(80,178,255,0.25)", strokeWidth: 1 }}
                  content={<CustomTooltip />}
                />
                <Legend
                  wrapperStyle={{
                    fontSize: 12,
                    color: "#d6d5ea",
                    paddingTop: 8,
                  }}
                  formatter={(value) => (
                    <span style={{ color: "#e5e8f3" }}>{value}</span>
                  )}
                />
                <Line
                  type="monotone"
                  dataKey="TOTAL"
                  name="TOTAL"
                  stroke="#d6d5ea"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "#d6d5ea", strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: "#d6d5ea" }}
                />
                <Line
                  type="monotone"
                  dataKey="OPEN"
                  name="OPEN"
                  stroke={PURPLE}
                  strokeWidth={2}
                  dot={{ r: 3, fill: PURPLE, strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: PURPLE }}
                />
                <Line
                  type="monotone"
                  dataKey="CLOSED"
                  name="CLOSED"
                  stroke={BLUE}
                  strokeWidth={2}
                  dot={{ r: 3, fill: BLUE, strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: BLUE }}
                />
              </LineChart>
            )}
          </MeasuredChartFrame>
        )}
      </article>
    </section>
  );
}